import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const API_BASE = process.env.REACT_APP_API_URL;

export default function LessonFeedback() {
  const { lessonId, studentId, classroomId } = useParams();

  // Data
  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [student, setStudent] = useState(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Editable fields
  const [feedback, setFeedback] = useState('');
  const [grade, setGrade] = useState(''); // string for input

  // AI state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError('');

        // Fetch lesson
        const { data: lessonData, error: lessonErr } = await supabase
          .from('lessons')
          .select('id, title, level, reading, writing_prompt, quiz')
          .eq('id', lessonId)
          .maybeSingle();

        if (lessonErr || !lessonData) throw lessonErr || new Error('Lesson not found');

        // Normalize quiz IDs
        const normalizedQuiz = Array.isArray(lessonData.quiz)
          ? lessonData.quiz.map((q, idx) => ({
              ...q,
              id: q.id || `q${idx}`,
              options: Array.isArray(q.options) ? q.options : [],
            }))
          : [];

        // Fetch progress
        const { data: prog, error: progErr } = await supabase
          .from('classroom_lesson_progress')
          .select('score, writing_response, teacher_feedback, teacher_score, answers, updated_at')
          .eq('lesson_id', lessonId)
          .eq('student_id', studentId)
          .eq('classroom_id', classroomId)
          .maybeSingle();

        if (progErr) throw progErr;

        // Fetch student (for header)
        const { data: stu, error: stuErr } = await supabase
          .from('profiles')
          .select('id, username')
          .eq('id', studentId)
          .maybeSingle();

        if (stuErr) throw stuErr;

        if (!mounted) return;

        setLesson({ ...lessonData, quiz: normalizedQuiz });
        setProgress(prog || {});
        setStudent(stu || null);
        setFeedback((prog && prog.teacher_feedback) || '');
        setGrade(
          prog && typeof prog.teacher_score === 'number'
            ? String(prog.teacher_score)
            : ''
        );
      } catch (e) {
        console.error(e);
        if (mounted) setError('Failed to load lesson or student progress.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [lessonId, studentId, classroomId]);

  const wordCount = useMemo(() => {
    const text = progress?.writing_response || '';
    return text.trim() ? text.trim().split(/\s+/).length : 0;
  }, [progress?.writing_response]);

  const handleSave = async () => {
    setSaving(true);
    setError('');

    const numericGrade =
      grade.trim() === '' ? null : Math.max(0, Math.min(100, parseInt(grade, 10)));

    try {
      const { error: updErr } = await supabase
        .from('classroom_lesson_progress')
        .update({
          teacher_feedback: feedback || null,
          teacher_score: numericGrade,
        })
        .eq('lesson_id', lessonId)
        .eq('student_id', studentId)
        .eq('classroom_id', classroomId);

      if (updErr) throw updErr;

      // Optimistic UI update
      setProgress((prev) => ({
        ...(prev || {}),
        teacher_feedback: feedback || null,
        teacher_score: numericGrade,
      }));
    } catch (e) {
      console.error(e);
      setError('Failed to save feedback.');
    } finally {
      setSaving(false);
    }
  };

  const handleAIDraft = async () => {
    if (!lesson?.writing_prompt || !progress?.writing_response) {
      setAiError('Need a writing prompt and a student response to suggest feedback.');
      return;
    }
    setAiLoading(true);
    setAiError('');

    try {
      const res = await fetch(`${API_BASE}/api/lesson-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: lesson.writing_prompt,
          input: progress.writing_response,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'AI feedback failed');
      // Populate fields; teacher can edit before saving
      if (typeof data.feedback === 'string') setFeedback(data.feedback);
      if (typeof data.score === 'number') setGrade(String(data.score));
    } catch (e) {
      console.error(e);
      setAiError('Could not generate AI suggestion.');
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="h-8 w-2/3 bg-gray-100 rounded animate-pulse" />
        <div className="h-24 w-full bg-gray-100 rounded animate-pulse" />
        <div className="h-56 w-full bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <div className="rounded border border-red-200 bg-red-50 p-3 text-red-700">
          {error || 'Lesson not found.'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-2xl font-bold text-gray-900">{lesson.title}</h2>
            <p className="text-sm text-gray-600 mt-1">
              {student?.username ? `Student: ${student.username}` : 'Student'}
              {' · '}Level: {lesson.level}
            </p>
          </div>
          <div className="text-right text-sm text-gray-600">
            {progress?.updated_at && (
              <div>Last activity: {new Date(progress.updated_at).toLocaleString()}</div>
            )}
          </div>
        </div>
      </div>

      {/* Student Writing */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-3">
        <h3 className="text-lg font-semibold text-gray-800">Student’s Writing Response</h3>
        {lesson.writing_prompt && (
          <p className="text-sm text-gray-600 italic mb-1">
            Prompt: {lesson.writing_prompt}
          </p>
        )}
        <div className="rounded border bg-gray-50 p-4 text-gray-900 whitespace-pre-wrap">
          {progress?.writing_response || 'No response submitted.'}
        </div>
        <div className="text-xs text-gray-500">
          {wordCount} word{wordCount === 1 ? '' : 's'}
        </div>
      </div>

      {/* Quiz Review (if present) */}
      {Array.isArray(lesson.quiz) && lesson.quiz.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Quiz Review</h3>
          <ul className="space-y-3">
            {lesson.quiz.map((q) => {
              const studentAns = progress?.answers?.[q.id];
              const isCorrect = studentAns === q.correct;
              return (
                <li key={q.id} className="rounded border border-gray-200 p-3">
                  <p className="font-medium text-gray-900">{q.text}</p>
                  <p className="text-sm mt-1">
                    <span className="text-gray-600">Student:</span>{' '}
                    <span className={isCorrect ? 'text-green-700 font-medium' : 'text-red-600 font-medium'}>
                      {studentAns ?? '—'}
                    </span>
                    {' · '}
                    <span className="text-gray-600">Correct:</span>{' '}
                    <span className="font-medium">{q.correct}</span>
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Teacher Feedback + Grade */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Teacher Feedback</h3>
          <button
            onClick={handleAIDraft}
            disabled={aiLoading}
            className="text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            title="Suggest feedback and a score from AI"
          >
            {aiLoading ? 'Suggesting…' : 'AI Suggest'}
          </button>
        </div>
        {aiError && (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
            {aiError}
          </div>
        )}
        <textarea
          rows={6}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded"
          placeholder="Write clear, impersonal, criteria-referenced feedback…"
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div className="sm:col-span-1">
            <label className="block text-sm text-gray-700 mb-1">Grade (/100)</label>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={100}
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded"
              placeholder="e.g., 84"
            />
          </div>
          <div className="sm:col-span-2">
            <input
              type="range"
              min={0}
              max={100}
              value={grade === '' ? 0 : Number(grade)}
              onChange={(e) => setGrade(String(e.target.value))}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2 rounded disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Feedback'}
          </button>
          {error && (
            <span className="text-red-600 text-sm">{error}</span>
          )}
          {!saving && !error && (progress?.teacher_feedback || progress?.teacher_score != null) && (
            <span className="text-green-700 text-sm">Saved ✓</span>
          )}
        </div>
      </div>
    </div>
  );
}
